import { createContext, useContext, useEffect, useMemo, useState, useRef, useCallback, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getCMSContent, saveCMSDraftContent, publishCMSContent, discardCMSDraftContent, getSettings } from '../utils/api';
import { initialCMSContent, type CMSContent } from '../data/cms';
import { useAdminAuth } from './AdminAuthContext';
import { toast } from 'react-hot-toast';

export type AutoSaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export interface StorefrontDataContextValue {
  cms: any;
  settings: Record<string, any>;
  cdnBaseUrl: string;
  isReady: boolean;
  // Live Editor States & Handlers
  isAdmin: boolean;
  isEditMode: boolean;
  isPreviewMode: boolean;
  setIsEditMode: (mode: boolean) => void;
  setIsPreviewMode: (mode: boolean) => void;
  saveStatus: AutoSaveStatus;
  lastSavedTime: string | null;
  hasUnpublishedChanges: boolean;
  updateCmsField: (section: string, field: string, value: any) => void;
  updateCmsSection: (section: string, value: any) => void;
  updateWholeCms: (updater: (prev: any) => any) => void;
  saveDraftManual: () => Promise<boolean>;
  publishToLive: () => Promise<boolean>;
  discardDraftChanges: () => Promise<boolean>;
  viewSource?: 'published' | 'draft';
  setViewSource?: (source: 'published' | 'draft') => void;
  // Master Drawer & Global Sections
  isMasterDrawerOpen: boolean;
  activeMasterTab: 'footer' | 'cta' | 'header';
  openMasterDrawer: (tab?: 'footer' | 'cta' | 'header') => void;
  closeMasterDrawer: () => void;
  addFooterColumn: (title?: string) => void;
  removeFooterColumn: (columnIndex: number) => void;
  updateFooterColumnTitle: (columnIndex: number, title: string) => void;
  addFooterLinkItem: (columnIndex: number, item?: { label: string; url: string }) => void;
  removeFooterLinkItem: (columnIndex: number, itemIndex: number) => void;
  updateFooterLinkItem: (columnIndex: number, itemIndex: number, field: 'label' | 'url', value: string) => void;
}

const CACHE_CMS_KEY = 'secera_cms_cache_v1';
const CACHE_SETTINGS_KEY = 'secera_settings_cache_v1';

const mergeWithInitialCMS = (data: any) => {
  if (!data || typeof data !== 'object') return initialCMSContent;
  return {
    ...initialCMSContent,
    ...data,
    hero: { ...initialCMSContent.hero, ...data.hero },
    showcase: { ...initialCMSContent.showcase, ...data.showcase },
    testimonials: { ...initialCMSContent.testimonials, ...data.testimonials },
    ugc: { ...initialCMSContent.ugc, ...data.ugc },
    faq: { ...initialCMSContent.faq, ...data.faq },
    cta: { ...initialCMSContent.cta, ...data.cta },
    features: { ...initialCMSContent.features, ...data.features },
    footer: { ...initialCMSContent.footer, ...data.footer },
    global: { ...initialCMSContent.global, ...data.global },
    stylePreference: { ...initialCMSContent.stylePreference, ...data.stylePreference },
    seo: { ...initialCMSContent.seo, ...data.seo },
    marquee: { ...initialCMSContent.marquee, ...data.marquee },
    notifications: { ...initialCMSContent.notifications, ...data.notifications },
    header: { ...initialCMSContent.header, ...data.header },
  };
};

const getInitialCms = () => {
  try {
    const cached = localStorage.getItem(CACHE_CMS_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      return mergeWithInitialCMS(parsed);
    }
  } catch (e) {
    console.error('Error loading CMS cache:', e);
  }
  return initialCMSContent;
};

const getInitialSettings = () => {
  try {
    const cached = localStorage.getItem(CACHE_SETTINGS_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (parsed && typeof parsed === 'object') {
        return parsed;
      }
    }
  } catch (e) {
    console.error('Error loading settings cache:', e);
  }
  return {};
};

const StorefrontDataContext = createContext<StorefrontDataContextValue | null>(null);

export function StorefrontDataProvider({ children }: { children: ReactNode }) {
  const adminAuth = useAdminAuth();
  const isAdmin = Boolean(adminAuth?.isAuthenticated && adminAuth?.admin);
  const location = useLocation();
  const navigate = useNavigate();

  // URL query params
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const initialEditRequested = searchParams.get('edit') === 'true';
  const initialPreviewRequested = searchParams.get('preview') === 'true';

  const [isEditMode, setIsEditModeState] = useState<boolean>(initialEditRequested && isAdmin);
  const [isPreviewMode, setIsPreviewModeState] = useState<boolean>(initialPreviewRequested && isAdmin);

  const [publishedCms, setPublishedCms] = useState<any>(initialCMSContent);
  const [draftCms, setDraftCms] = useState<any>(getInitialCms);
  const [viewSource, setViewSource] = useState<'published' | 'draft'>(
    (initialEditRequested || initialPreviewRequested) && isAdmin ? 'draft' : 'published'
  );

  const [settings, setSettings] = useState<Record<string, any>>(getInitialSettings);
  const [isReady, setIsReady] = useState(true);

  // Live Editor status
  const [saveStatus, setSaveStatus] = useState<AutoSaveStatus>('idle');
  const [lastSavedTime, setLastSavedTime] = useState<string | null>(null);
  const [hasUnpublishedChanges, setHasUnpublishedChanges] = useState<boolean>(false);

  // Active CMS data based on mode & viewSource (Strict Separation + Quick Preview)
  const cms = useMemo(() => {
    if (!isAdmin) return publishedCms;
    if (isEditMode || isPreviewMode || viewSource === 'draft') {
      return draftCms;
    }
    return publishedCms;
  }, [isAdmin, isEditMode, isPreviewMode, viewSource, draftCms, publishedCms]);

  const cmsRef = useRef(draftCms);
  cmsRef.current = draftCms;

  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Sync edit mode state if admin logs in or URL changes
  useEffect(() => {
    if (isAdmin && initialEditRequested) {
      setIsEditModeState(true);
      setViewSource('draft');
    }
  }, [isAdmin, initialEditRequested]);

  const setIsEditMode = useCallback((mode: boolean) => {
    setIsEditModeState(mode);
    const newParams = new URLSearchParams(location.search);
    if (mode) {
      newParams.set('edit', 'true');
      newParams.delete('preview');
      setIsPreviewModeState(false);
      setViewSource('draft');
    } else {
      newParams.delete('edit');
      setViewSource('published');
    }
    const searchStr = newParams.toString() ? `?${newParams.toString()}` : '';
    navigate(`${location.pathname}${searchStr}`, { replace: true });
  }, [location.pathname, location.search, navigate]);

  const setIsPreviewMode = useCallback((preview: boolean) => {
    setIsPreviewModeState(preview);
    const newParams = new URLSearchParams(location.search);
    if (preview) {
      newParams.set('preview', 'true');
      newParams.delete('edit');
      setIsEditModeState(false);
      setViewSource('draft');
    } else {
      newParams.delete('preview');
    }
    const searchStr = newParams.toString() ? `?${newParams.toString()}` : '';
    navigate(`${location.pathname}${searchStr}`, { replace: true });
  }, [location.pathname, location.search, navigate]);

  // Load data: If admin, query draft support & live data
  useEffect(() => {
    let isMounted = true;

    async function loadStorefrontData() {
      try {
        if (isAdmin) {
          const [draftData, liveData, settingsData] = await Promise.all([
            getCMSContent('main_site', { draft: true }),
            getCMSContent('main_site'),
            getSettings(),
          ]);

          if (!isMounted) return;

          const mergedLive = mergeWithInitialCMS(liveData);
          setPublishedCms(mergedLive);

          const hasDraft = Boolean(draftData && draftData._isDraft);
          setHasUnpublishedChanges(hasDraft);

          const mergedDraft = hasDraft ? mergeWithInitialCMS(draftData) : mergedLive;
          setDraftCms(mergedDraft);
          cmsRef.current = mergedDraft;

          try {
            localStorage.setItem(CACHE_CMS_KEY, JSON.stringify(mergedDraft));
          } catch (e) {
            console.error('Failed to save CMS cache:', e);
          }

          if (settingsData && typeof settingsData === 'object') {
            setSettings(settingsData);
            try {
              localStorage.setItem(CACHE_SETTINGS_KEY, JSON.stringify(settingsData));
            } catch (e) {
              console.error('Failed to save settings cache:', e);
            }
          }
        } else {
          const [liveData, settingsData] = await Promise.all([
            getCMSContent('main_site'),
            getSettings(),
          ]);

          if (!isMounted) return;

          const mergedLive = mergeWithInitialCMS(liveData);
          setPublishedCms(mergedLive);
          setDraftCms(mergedLive);
          cmsRef.current = mergedLive;
          setHasUnpublishedChanges(false);

          try {
            localStorage.setItem(CACHE_CMS_KEY, JSON.stringify(liveData));
          } catch (e) {
            console.error('Failed to save CMS cache:', e);
          }

          if (settingsData && typeof settingsData === 'object') {
            setSettings(settingsData);
            try {
              localStorage.setItem(CACHE_SETTINGS_KEY, JSON.stringify(settingsData));
            } catch (e) {
              console.error('Failed to save settings cache:', e);
            }
          }
        }
      } catch (error) {
        console.error('Failed to load storefront data:', error);
      } finally {
        if (isMounted) {
          setIsReady(true);
        }
      }
    }

    loadStorefrontData();

    return () => {
      isMounted = false;
    };
  }, [isAdmin]);

  // Execute actual save draft
  const executeSaveDraft = useCallback(async (contentToSave: any) => {
    if (!isAdmin) return false;
    setSaveStatus('saving');
    try {
      const success = await saveCMSDraftContent('main_site', contentToSave);
      if (success) {
        setSaveStatus('saved');
        setHasUnpublishedChanges(true);
        const timeStr = new Date().toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' });
        setLastSavedTime(timeStr);
        return true;
      } else {
        setSaveStatus('error');
        return false;
      }
    } catch (err) {
      console.error('Error saving draft:', err);
      setSaveStatus('error');
      return false;
    }
  }, [isAdmin]);

  // Trigger auto save with 2.5s debounce
  const triggerDebouncedAutoSave = useCallback((updatedCms: any) => {
    if (!isAdmin) return;
    setSaveStatus('saving');
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    autoSaveTimerRef.current = setTimeout(() => {
      executeSaveDraft(updatedCms);
    }, 2500);
  }, [isAdmin, executeSaveDraft]);

  // Clean timer on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, []);

  // Update a single nested field (e.g. section='hero', field='title', value='...')
  const updateCmsField = useCallback((section: string, field: string, value: any) => {
    setViewSource('draft');
    setDraftCms((prev: any) => {
      const prevSection = prev[section] || {};
      const updated = {
        ...prev,
        [section]: {
          ...prevSection,
          [field]: value
        }
      };
      triggerDebouncedAutoSave(updated);
      return updated;
    });
  }, [triggerDebouncedAutoSave]);

  // Update an entire section (e.g. section='faq', value={ title: '...', items: [...] })
  const updateCmsSection = useCallback((section: string, value: any) => {
    setViewSource('draft');
    setDraftCms((prev: any) => {
      const updated = {
        ...prev,
        [section]: value
      };
      triggerDebouncedAutoSave(updated);
      return updated;
    });
  }, [triggerDebouncedAutoSave]);

  // Update whole CMS object via updater function
  const updateWholeCms = useCallback((updater: (prev: any) => any) => {
    setViewSource('draft');
    setDraftCms((prev: any) => {
      const updated = updater(prev);
      triggerDebouncedAutoSave(updated);
      return updated;
    });
  }, [triggerDebouncedAutoSave]);

  // Manual save draft button handler
  const saveDraftManual = useCallback(async () => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    const success = await executeSaveDraft(cmsRef.current);
    if (success) {
      toast.success('Draft berhasil disimpan');
    } else {
      toast.error('Gagal menyimpan draft');
    }
    return success;
  }, [executeSaveDraft]);

  // Keyboard shortcut: Cmd+S (Mac) / Ctrl+S (Windows/Linux) to manually save draft
  useEffect(() => {
    if (!isAdmin || !isEditMode) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        e.stopImmediatePropagation();
        saveDraftManual();
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [isAdmin, isEditMode, saveDraftManual]);

  // Publish to public live
  const publishToLive = useCallback(async () => {
    if (!isAdmin) return false;
    setSaveStatus('saving');
    try {
      const success = await publishCMSContent('main_site', cmsRef.current);
      if (success) {
        setSaveStatus('saved');
        setHasUnpublishedChanges(false);
        setPublishedCms(cmsRef.current);
        const timeStr = new Date().toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' });
        setLastSavedTime(timeStr);
        try {
          localStorage.setItem(CACHE_CMS_KEY, JSON.stringify(cmsRef.current));
        } catch (e) {
          console.error('Failed to update public cache:', e);
        }
        toast.success('🎉 Halaman berhasil dipublikasikan ke publik!');
        return true;
      } else {
        setSaveStatus('error');
        toast.error('Gagal mempublikasikan ke publik');
        return false;
      }
    } catch (err) {
      console.error('Error publishing:', err);
      setSaveStatus('error');
      toast.error('Terjadi kesalahan saat mempublikasikan');
      return false;
    }
  }, [isAdmin]);

  // Discard draft changes
  const discardDraftChanges = useCallback(async () => {
    if (!isAdmin) return false;
    try {
      const success = await discardCMSDraftContent('main_site');
      if (success) {
        setDraftCms(publishedCms);
        cmsRef.current = publishedCms;
        setHasUnpublishedChanges(false);
        setSaveStatus('idle');
        setViewSource('published');
        toast.success('Draft dibatalkan, kembali ke versi publik');
        return true;
      } else {
        toast.error('Gagal membatalkan draft');
        return false;
      }
    } catch (err) {
      console.error('Error discarding draft:', err);
      toast.error('Gagal membatalkan draft');
      return false;
    }
  }, [isAdmin, publishedCms]);

  // Global Master Drawer state
  const [isMasterDrawerOpen, setIsMasterDrawerOpen] = useState(false);
  const [activeMasterTab, setActiveMasterTab] = useState<'footer' | 'cta' | 'header'>('footer');

  const openMasterDrawer = useCallback((tab: 'footer' | 'cta' | 'header' = 'footer') => {
    setActiveMasterTab(tab);
    setIsMasterDrawerOpen(true);
    if (tab === 'footer') {
      setTimeout(() => {
        window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
      }, 150);
    } else if (tab === 'cta') {
      setTimeout(() => {
        document.getElementById('cta-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 150);
    }
  }, []);

  const closeMasterDrawer = useCallback(() => {
    setIsMasterDrawerOpen(false);
  }, []);

  // Check openMaster in URL query params on mount/URL change
  useEffect(() => {
    const openMasterParam = searchParams.get('openMaster') || searchParams.get('master');
    if (openMasterParam && isAdmin) {
      if (['footer', 'cta', 'header'].includes(openMasterParam)) {
        openMasterDrawer(openMasterParam as 'footer' | 'cta' | 'header');
      }
    }
  }, [searchParams, isAdmin, openMasterDrawer]);

  // Dynamic Footer Link Columns Helpers
  const addFooterColumn = useCallback((title: string = 'Kolom Baru') => {
    setViewSource('draft');
    setDraftCms((prev: any) => {
      const currentFooter = prev.footer || {};
      const currentLinks = Array.isArray(currentFooter.links) ? currentFooter.links : [];
      const newLinks = [
        ...currentLinks,
        {
          id: `col-${Date.now()}`,
          title,
          items: [
            { id: `item-${Date.now()}`, label: 'Halaman Baru', url: '/shop' }
          ]
        }
      ];
      const updated = {
        ...prev,
        footer: {
          ...currentFooter,
          links: newLinks
        }
      };
      triggerDebouncedAutoSave(updated);
      return updated;
    });
  }, [triggerDebouncedAutoSave]);

  const removeFooterColumn = useCallback((columnIndex: number) => {
    setViewSource('draft');
    setDraftCms((prev: any) => {
      const currentFooter = prev.footer || {};
      const currentLinks = Array.isArray(currentFooter.links) ? currentFooter.links : [];
      const newLinks = currentLinks.filter((_, idx) => idx !== columnIndex);
      const updated = {
        ...prev,
        footer: {
          ...currentFooter,
          links: newLinks
        }
      };
      triggerDebouncedAutoSave(updated);
      return updated;
    });
  }, [triggerDebouncedAutoSave]);

  const updateFooterColumnTitle = useCallback((columnIndex: number, title: string) => {
    setViewSource('draft');
    setDraftCms((prev: any) => {
      const currentFooter = prev.footer || {};
      const currentLinks = Array.isArray(currentFooter.links) ? [...currentFooter.links] : [];
      if (!currentLinks[columnIndex]) return prev;
      currentLinks[columnIndex] = {
        ...currentLinks[columnIndex],
        title
      };
      const updated = {
        ...prev,
        footer: {
          ...currentFooter,
          links: currentLinks
        }
      };
      triggerDebouncedAutoSave(updated);
      return updated;
    });
  }, [triggerDebouncedAutoSave]);

  const addFooterLinkItem = useCallback((columnIndex: number, item?: { label: string; url: string }) => {
    setViewSource('draft');
    setDraftCms((prev: any) => {
      const currentFooter = prev.footer || {};
      const currentLinks = Array.isArray(currentFooter.links) ? [...currentFooter.links] : [];
      if (!currentLinks[columnIndex]) return prev;
      const currentItems = Array.isArray(currentLinks[columnIndex].items) ? [...currentLinks[columnIndex].items] : [];
      currentItems.push({
        id: `item-${Date.now()}`,
        label: item?.label || 'Tautan Baru',
        url: item?.url || '/shop'
      });
      currentLinks[columnIndex] = {
        ...currentLinks[columnIndex],
        items: currentItems
      };
      const updated = {
        ...prev,
        footer: {
          ...currentFooter,
          links: currentLinks
        }
      };
      triggerDebouncedAutoSave(updated);
      return updated;
    });
  }, [triggerDebouncedAutoSave]);

  const removeFooterLinkItem = useCallback((columnIndex: number, itemIndex: number) => {
    setViewSource('draft');
    setDraftCms((prev: any) => {
      const currentFooter = prev.footer || {};
      const currentLinks = Array.isArray(currentFooter.links) ? [...currentFooter.links] : [];
      if (!currentLinks[columnIndex]) return prev;
      const currentItems = Array.isArray(currentLinks[columnIndex].items) ? [...currentLinks[columnIndex].items] : [];
      currentItems.splice(itemIndex, 1);
      currentLinks[columnIndex] = {
        ...currentLinks[columnIndex],
        items: currentItems
      };
      const updated = {
        ...prev,
        footer: {
          ...currentFooter,
          links: currentLinks
        }
      };
      triggerDebouncedAutoSave(updated);
      return updated;
    });
  }, [triggerDebouncedAutoSave]);

  const updateFooterLinkItem = useCallback((columnIndex: number, itemIndex: number, field: 'label' | 'url', value: string) => {
    setViewSource('draft');
    setDraftCms((prev: any) => {
      const currentFooter = prev.footer || {};
      const currentLinks = Array.isArray(currentFooter.links) ? [...currentFooter.links] : [];
      if (!currentLinks[columnIndex]) return prev;
      const currentItems = Array.isArray(currentLinks[columnIndex].items) ? [...currentLinks[columnIndex].items] : [];
      if (!currentItems[itemIndex]) return prev;
      currentItems[itemIndex] = {
        ...currentItems[itemIndex],
        [field]: value
      };
      currentLinks[columnIndex] = {
        ...currentLinks[columnIndex],
        items: currentItems
      };
      const updated = {
        ...prev,
        footer: {
          ...currentFooter,
          links: currentLinks
        }
      };
      triggerDebouncedAutoSave(updated);
      return updated;
    });
  }, [triggerDebouncedAutoSave]);

  const value = useMemo(
    () => ({
      cms,
      settings,
      cdnBaseUrl: settings?.cdn_base_url || '',
      isReady,
      isAdmin,
      isEditMode,
      isPreviewMode,
      setIsEditMode,
      setIsPreviewMode,
      saveStatus,
      lastSavedTime,
      hasUnpublishedChanges,
      updateCmsField,
      updateCmsSection,
      updateWholeCms,
      saveDraftManual,
      publishToLive,
      discardDraftChanges,
      viewSource,
      setViewSource,
      isMasterDrawerOpen,
      activeMasterTab,
      openMasterDrawer,
      closeMasterDrawer,
      addFooterColumn,
      removeFooterColumn,
      updateFooterColumnTitle,
      addFooterLinkItem,
      removeFooterLinkItem,
      updateFooterLinkItem
    }),
    [
      cms,
      settings,
      isReady,
      isAdmin,
      isEditMode,
      isPreviewMode,
      setIsEditMode,
      setIsPreviewMode,
      saveStatus,
      lastSavedTime,
      hasUnpublishedChanges,
      updateCmsField,
      updateCmsSection,
      updateWholeCms,
      saveDraftManual,
      publishToLive,
      discardDraftChanges,
      viewSource,
      setViewSource,
      isMasterDrawerOpen,
      activeMasterTab,
      openMasterDrawer,
      closeMasterDrawer,
      addFooterColumn,
      removeFooterColumn,
      updateFooterColumnTitle,
      addFooterLinkItem,
      removeFooterLinkItem,
      updateFooterLinkItem
    ]
  );

  return <StorefrontDataContext.Provider value={value}>{children}</StorefrontDataContext.Provider>;
}

export function useStorefrontData() {
  const context = useContext(StorefrontDataContext);

  if (!context) {
    throw new Error('useStorefrontData must be used within StorefrontDataProvider');
  }

  return context;
}
